/* eslint-disable max-lines-per-function */
import React from "react";
import moment from "moment";
import { Card, CardActionArea, CardActions, Chip, Grid } from "@material-ui/core";
import { InterestStatus } from "model/interest/status";
import {
  PropertiesPage_propertiesPage as PropertyInfo,
  PropertiesPage_propertiesPage_viewerSubmissions as Submission,
} from "types/gql-types";
import { LinkButton } from "components/haven-design";
import RemoveUserModal from "pages/clients/interests/list/remove-user-modal";
import RemoveDraftModal from "pages/clients/interests/list/remove-draft-modal";
import { toKabobCase } from "pages/dashboard/interests/utils";
import styles from "./properties.module.css";

const formatDate = (submittedAt: number | null) =>
  (submittedAt ? moment(new Date(submittedAt * 1000)).format("MM-DD-YY") : "");

const activeSubmissions = (app: Submission) =>
  (app.submittedAt !== null || app.submissionId !== null) &&
  app.currentStatus !== InterestStatus.Removed &&
  app.currentStatus !== InterestStatus.MovedIn;

const getActiveAppLink = (app: Submission) => (
  app.submissionId ? `/application/${app.submissionId}` : (
    `/dash/client/submissions/${app.interestListId}/${app.interestId}`));

const PropertyCardTop: React.FC<{ property: PropertyInfo }> = ({ property }) => (
  <div className={styles.cardTop}>
    <h2 style={{ color: "white" }}>{property.name}</h2>
    <div className={styles.orgName}>{property.orgName}</div>
    {Boolean(property.unitSizes.length) && (<div>
      {property.unitSizes.map((unitSize) => <Chip
        key={`unit-${toKabobCase(unitSize)}`}
        label={unitSize}
        size="small"
        color="primary"
        variant="outlined"
        style={{ margin: 1 }}
      />)}
    </div>)}
    <div>{property.address1}</div>
    <div>
      {property.city}, {property.state} {property.zip}
    </div>
  </div>
);

const PropertyCardAction: React.FC<{
  property: PropertyInfo,
  onApplicationRemoved: () => void
}> = ({ property, onApplicationRemoved }) => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [isDraftModalOpen, setDraftModalOpen] = React.useState(false);

  const movedIn = property.viewerSubmissions
    .map(x => x.currentStatus)
    .includes(InterestStatus.MovedIn);

  const previouslyRemoved = property.viewerSubmissions
    .map(x => x.currentStatus)
    .includes(InterestStatus.Removed);

  const activeApps = property.viewerSubmissions.filter(activeSubmissions);
  const now = new Date();
  const nowSec = Date.parse(now.toDateString()) / 1000;
  const activeApp =
    activeApps.length ? activeApps
      .sort((a, b) => (a.submittedAt || nowSec) - (b.submittedAt || nowSec))[0] : null;

  const handleOnRequestClose = (isRemoved: boolean) => {
    setModalOpen(false);
    setDraftModalOpen(false);
    if (isRemoved) onApplicationRemoved();
  };

  return (
    <Grid item container direction="column">
      {movedIn && (
        <Grid item>
          <p className="text-center">You&apos;re moved-in to this property</p>
        </Grid>
      )}

      {!movedIn && activeApp && (
        // eslint-disable-next-line no-undefined
        <Grid item>
          <p className="text-center">
            {activeApp.submittedAt ? `Applied ${formatDate(activeApp.submittedAt)}` : "Draft"}
          </p>
          <LinkButton
            color="secondary"
            to={getActiveAppLink(activeApp)}
            size="L"
            fullWidth
          >
            {activeApp.submittedAt ? "Update Application" : "Continue Application"}
          </LinkButton>
          <p className="text-center" style={{ paddingTop: 10 }}>
            {activeApp.submissionId ? (<a
              onClick={() => setDraftModalOpen(true)}
              role="button">Remove draft</a>) : (<a
              onClick={() => setModalOpen(true)}
              role="button">Remove me</a>)}

          </p>
          {activeApp.interestId && (
            <RemoveUserModal
              isOpen={isModalOpen}
              onRequestClose={handleOnRequestClose}
              interestListName={property.name || ""}
              interestId={activeApp.interestId}
            />)}
          {activeApp.submissionId && (
            <RemoveDraftModal
              isOpen={isDraftModalOpen}
              onRequestClose={handleOnRequestClose}
              submissionId={activeApp.submissionId}
              interestListName={property.name || ""}
              formType="Application"
            />
          )}
        </Grid>
      )}

      {!movedIn && !activeApp && (
        <LinkButton
          to={`${property.preAppUrl}?utm_source=HCprop`}
          color="primary"
          size="L"
          fullWidth
        >{ previouslyRemoved ? "Reapply Now" : "Apply Now"}</LinkButton>
      )}
    </Grid>
  );
};

const Property: React.FC<{
  property: PropertyInfo,
  onApplicationRemoved: () => void
}> = ({ property, onApplicationRemoved }) => (
  <Grid item key={property.id} xs={12} sm={12} md={4} lg={3}>
    <Card>
      <CardActionArea className={styles.actionArea}>
        <PropertyCardTop property={property} />
        <div className={styles.cardLower}>
          <div>
            <b><i className="fa fa-phone" /></b>
            <a href={`tel:${property.phone}`}>{property.phone}</a>
          </div>
          <div>
            <b><i className="fa fa-envelope" /></b>
            <a href={`mailto:${property.email}`}>{property.email}</a>
          </div>
        </div>
      </CardActionArea>
      <CardActions>
        <PropertyCardAction property={property} onApplicationRemoved={onApplicationRemoved} />
      </CardActions>
    </Card>
  </Grid>
);

export default Property;
